import { Box, Text, Tooltip } from '@mantine/core';
import { CheckCircle2, Lock } from 'lucide-react';
import type { FC } from 'react';
import { useHostContext } from '@/context/HostContext.tsx';
import type { StepperSetupStepKey, StepperSetupStepStatus } from '@/stepper';
import { cn } from '@/utils';
import { CLASS_NAMES } from '../../classNames';

interface StepperStepCardHeaderContentProps {
	isAgentProgressActive: boolean;
	isCompleted: boolean;
	isDisabled: boolean;
	isLocked?: boolean;
	number: number;
	stepKey: StepperSetupStepKey;
	status: StepperSetupStepStatus;
	title: string;
}

const StepperStepCardHeaderContent: FC<StepperStepCardHeaderContentProps> = ({
	isAgentProgressActive,
	isCompleted,
	isDisabled,
	isLocked = false,
	number,
	stepKey,
	status,
	title
}) => {
	const { localization } = useHostContext();
	const lockedTooltipLabel = localization.translate(
		'creator',
		'stepperOnboarding.steps.lockedTooltip'
	);

	return (
		<>
			{isCompleted ? (
				<CheckCircle2
					aria-hidden="true"
					className={CLASS_NAMES.stepIconCompleted}
					data-testid="stepper-step-card-completed-icon"
				/>
			) : (
				<Box
					aria-hidden="true"
					className={CLASS_NAMES.stepNumber}
					data-testid="stepper-step-card-number"
				>
					<span className="mt-0.5 text-sm leading-4">{number}</span>
				</Box>
			)}
			<Text
				className={cn(
					CLASS_NAMES.stepTitle,
					isDisabled
						? CLASS_NAMES.stepTitleDisabled
						: CLASS_NAMES.stepTitleByStatus[status],
					{
						'animate-text-shimmer': number === 5 && isAgentProgressActive
					}
				)}
				component="h2"
				data-testid="stepper-step-card-title"
			>
				{title}
			</Text>
			{isLocked && (
				<Tooltip
					label={
						<span data-testid="stepper-step-card-locked-tooltip">
							{lockedTooltipLabel}
						</span>
					}
					transitionProps={{ duration: 0 }}
					withArrow
				>
					<Lock
						aria-hidden="true"
						className={CLASS_NAMES.stepIconLocked}
						data-testid={`lock-icon-${stepKey}`}
					/>
				</Tooltip>
			)}
		</>
	);
};

export default StepperStepCardHeaderContent;
